import React from 'react';

interface SimpleTextFormatterProps {
  content: string;
  className?: string;
  mentionProcessor?: (content: string) => (string | React.ReactElement)[];
}

/**
 * Simple text formatter that handles basic formatting without markdown complexity
 * Designed for clean, readable forum posts
 */
export const SimpleTextFormatter: React.FC<SimpleTextFormatterProps> = ({ 
  content, 
  className = "",
  mentionProcessor
}) => {
  // Process the content to handle basic formatting
  const processContent = (text: string): JSX.Element[] => {
    // Normalize line breaks first
    const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Split by all line breaks and process each line
    const lines = normalizedText.split('\n');
    
    const elements: JSX.Element[] = [];
    let currentBulletList: string[] = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle bullet points
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
        currentBulletList.push(trimmedLine.substring(2));
        return;
      }
      
      // If we have accumulated bullet points, render them first
      if (currentBulletList.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="list-disc list-inside space-y-1 ml-2 mb-2">
            {currentBulletList.map((item, itemIndex) => (
              <li key={itemIndex} className="text-sm">
                {mentionProcessor ? mentionProcessor(item) : item}
              </li>
            ))}
          </ul>
        );
        currentBulletList = [];
      }
      
      // Handle regular lines (including empty lines)
      if (trimmedLine === '') {
        // Empty line becomes a line break
        elements.push(<br key={`br-${index}`} />);
      } else {
        // Non-empty line becomes text content
        const processedLine = mentionProcessor ? mentionProcessor(trimmedLine) : trimmedLine;
        elements.push(
          <React.Fragment key={`line-${index}`}>
            {processedLine}
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        );
      }
    });
    
    // Handle any remaining bullet points
    if (currentBulletList.length > 0) {
      elements.push(
        <ul key="list-final" className="list-disc list-inside space-y-1 ml-2">
          {currentBulletList.map((item, itemIndex) => (
            <li key={itemIndex} className="text-sm">
              {mentionProcessor ? mentionProcessor(item) : item}
            </li>
          ))}
        </ul>
      );
    }
    
    return [<div key="content">{elements}</div>];
  };

  return (
    <div className={`text-sm leading-relaxed ${className}`}>
      {processContent(content)}
    </div>
  );
};

export default SimpleTextFormatter; 